import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { Map as MapIcon, List, Search, GripVertical } from 'lucide-react';
type ViewMode = 'map' | 'list';
type ZoneType = 'Germany' | 'Spain' | 'UK' | 'France' | 'Italy' | 'Netherlands' | 'Belgium' | 'Austria';
export function MarketZonesPanel() {
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [activeZone, setActiveZone] = useState<ZoneType>('Germany');
  const [searchQuery, setSearchQuery] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const zones: {
    name: ZoneType;
    code: string;
    active: boolean;
    coords: [number, number];
  }[] = [{
    name: 'Germany',
    code: 'DE',
    active: true,
    coords: [10.4515, 51.1657]
  }, {
    name: 'France',
    code: 'FR',
    active: true,
    coords: [2.2137, 46.2276]
  }, {
    name: 'UK',
    code: 'UK',
    active: true,
    coords: [-3.436, 55.3781]
  }, {
    name: 'Spain',
    code: 'ES',
    active: true,
    coords: [-3.7492, 40.4637]
  }, {
    name: 'Italy',
    code: 'IT',
    active: true,
    coords: [12.5674, 41.8719]
  }, {
    name: 'Netherlands',
    code: 'NL',
    active: true,
    coords: [5.2913, 52.1326]
  }, {
    name: 'Belgium',
    code: 'BE',
    active: true,
    coords: [4.4699, 50.5039]
  }, {
    name: 'Austria',
    code: 'AT',
    active: true,
    coords: [14.5501, 47.5162]
  }];
  const filteredZones = zones.filter(z => z.name.toLowerCase().includes(searchQuery.toLowerCase()) || z.code.toLowerCase().includes(searchQuery.toLowerCase()));
  useEffect(() => {
    if (viewMode !== 'map' || !mapContainerRef.current) return;
    let mounted = true;
    const loadMapbox = async () => {
      try {
        // Check if already loaded
        // @ts-ignore
        if (window.mapboxgl) {
          if (mounted) initializeMap();
          return;
        }
        // Load CSS
        const link = document.createElement('link');
        link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        // Load JS
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js';
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
        if (mounted) {
          setTimeout(() => initializeMap(), 100);
        }
      } catch (error) {
        console.error('Failed to load Mapbox:', error);
      }
    };
    loadMapbox();
    return () => {
      mounted = false;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      // FIX: Access the marker property from each object before calling remove()
      markersRef.current.forEach(({
        marker
      }) => {
        if (marker && marker.remove) {
          marker.remove();
        }
      });
      markersRef.current = [];
    };
  }, [viewMode]);
  const initializeMap = () => {
    // @ts-ignore
    const mapboxgl = window.mapboxgl;
    if (!mapboxgl || !mapContainerRef.current || mapRef.current) return;
    console.log('Initializing map...');
    try {
      // Use the token from the provided URL
      mapboxgl.accessToken = 'pk.eyJ1IjoibWFydmluZ2FibGVyIiwiYSI6ImNtODRpaW55NjBzdjcyanM3a2l3bDAxYXAifQ.3zrJdRYQt-lD-cP8phh1dw';
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [10, 50],
        zoom: 3.5,
        minZoom: 3,
        maxZoom: 6
      });
      mapRef.current.addControl(new mapboxgl.NavigationControl({
        showCompass: false
      }), 'top-right');
      mapRef.current.on('load', () => {
        console.log('Map loaded successfully');
        setMapLoaded(true);
        // Add markers
        zones.forEach(zone => {
          const el = document.createElement('div');
          el.style.cssText = `
            width: 32px;
            height: 32px;
            background: ${zone.name === activeZone ? '#ff006b' : '#ffffff'};
            border: 2px solid ${zone.name === activeZone ? '#ff006b' : '#cbd5e1'};
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: bold;
            color: ${zone.name === activeZone ? '#ffffff' : '#64748b'};
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            transition: background 0.2s, border-color 0.2s, color 0.2s;
          `;
          el.textContent = zone.code;
          el.dataset.zone = zone.name;
          el.addEventListener('click', () => {
            setActiveZone(zone.name);
          });
          const marker = new mapboxgl.Marker({
            element: el,
            anchor: 'center'
          }).setLngLat(zone.coords).addTo(mapRef.current);
          markersRef.current.push({
            marker,
            zone: zone.name,
            element: el
          });
        });
      });
      mapRef.current.on('error', (e: any) => {
        console.error('Map error:', e);
      });
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  };
  // Update marker styles when active zone changes
  useEffect(() => {
    if (!mapLoaded || markersRef.current.length === 0) return;
    markersRef.current.forEach(({
      element,
      zone
    }) => {
      const isActive = zone === activeZone;
      element.style.background = isActive ? '#ff006b' : '#ffffff';
      element.style.borderColor = isActive ? '#ff006b' : '#cbd5e1';
      element.style.color = isActive ? '#ffffff' : '#64748b';
    });
  }, [activeZone, mapLoaded]);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 flex flex-col h-full shadow-sm shadow-slate-200/50 relative group" data-id="element-381">
      {/* Drag Handle */}
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-400 z-20" data-id="element-382">
        <GripVertical size={16} data-id="element-383" />
      </div>

      <div className="flex items-center justify-between mb-4 relative z-10" data-id="element-384">
        <div data-id="element-385">
          <h3 className="text-lg font-semibold text-slate-900" data-id="element-386">Market Zones</h3>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-387">
            1 selected:{' '}
            <span className="text-pink-600 font-medium" data-id="element-388">{activeZone}</span>
          </p>
        </div>
        <div className="flex bg-slate-100/80 rounded-lg p-0.5 border border-slate-200/50" data-id="element-389">
          <button onClick={() => setViewMode('map')} className={`p-1.5 rounded-md transition-all ${viewMode === 'map' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-390">
            <MapIcon size={14} data-id="element-391" />
          </button>
          <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-392">
            <List size={14} data-id="element-393" />
          </button>
        </div>
      </div>

      {viewMode === 'list' && <div className="mb-3 relative z-10" data-id="element-394">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-395" />
          <input type="text" placeholder="Search zones..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-1.5 text-xs bg-white/50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500/50 transition-all" data-id="element-396" />
        </div>}

      <div className="flex-1 overflow-hidden relative" data-id="element-397">
        {viewMode === 'map' ? <div className="w-full h-full rounded-lg overflow-hidden border border-slate-100 bg-slate-50 relative" data-id="element-398">
            <div ref={mapContainerRef} className="absolute inset-0" data-id="element-399" />
            {!mapLoaded && <div className="absolute inset-0 flex items-center justify-center bg-slate-50 z-10" data-id="element-400">
                <div className="flex flex-col items-center gap-2" data-id="element-401">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-pink-500/20 border-t-pink-500" data-id="element-402" />
                  <p className="text-xs text-slate-500" data-id="element-403">Loading map...</p>
                </div>
              </div>}
          </div> : <div className="h-full overflow-y-auto custom-scrollbar space-y-1 pr-1" data-id="element-404">
            {filteredZones.map(zone => <button key={zone.code} onClick={() => setActiveZone(zone.name)} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${activeZone === zone.name ? 'bg-gradient-to-r from-pink-500/10 to-purple-500/10 text-slate-900 border border-pink-500/20' : 'bg-transparent hover:bg-slate-50 text-slate-600'}`} data-id="element-405">
                <div className="flex items-center gap-2" data-id="element-406">
                  <span className={`w-6 h-4 rounded flex items-center justify-center text-[9px] font-bold ${activeZone === zone.name ? 'bg-pink-500 text-white' : 'bg-slate-200 text-slate-600'}`} data-id="element-407">
                    {zone.code}
                  </span>
                  {zone.name}
                </div>
                {activeZone === zone.name && <div className="w-1.5 h-1.5 rounded-full bg-pink-500 shadow-[0_0_8px_rgba(236,72,153,0.6)]" data-id="element-408" />}
              </button>)}
          </div>}
      </div>

      {viewMode === 'map' && <div className="mt-3 flex items-center justify-between text-[10px] text-slate-400 relative z-10" data-id="element-409">
          <span data-id="element-410">Pan & zoom to explore</span>
          <span data-id="element-411">Powered by Mapbox</span>
        </div>}
    </motion.div>;
}